/**
 * What the spend chart's hover label says, kept out of JSX so it can be tested.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The date a bucket covers, in the viewer's language.
 *
 * A bucket is one CALENDAR day in the viewer's own timezone — the server buckets
 * on the offset the client sent — so a single date names it exactly. That is
 * the whole reason the buckets are not rolling 24-hour windows: those straddled
 * two dates and no label could have been true of both halves.
 *
 * The year is omitted. Every bucket is within the last thirty days, so it adds
 * a column of noise to thirty labels to disambiguate nothing.
 */
export function bucketDateLabel(firstDayStartedAt: number, index: number, locale: string): string {
  return new Date(firstDayStartedAt + index * DAY_MS).toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Where the hover label sits, as a percentage across the chart, and how it is
 * anchored there.
 *
 * A label centred on the first bar hangs off the left edge of the chart and
 * over the figures beside it; centred on the last it runs past the right. The
 * three anchors are what keeps it inside its own box without measuring
 * anything, which a chart of fixed-width bars does not need to do.
 */
export function bucketTipAnchor(
  index: number,
  count: number
): { leftPercent: number; align: 'start' | 'center' | 'end' } {
  const leftPercent = count > 0 ? ((index + 0.5) / count) * 100 : 0;
  if (index <= 2) return { leftPercent, align: 'start' };
  if (index >= count - 3) return { leftPercent, align: 'end' };
  return { leftPercent, align: 'center' };
}
